import { useDraggable } from '@dnd-kit/core'
import { Archive, ArrowLeft, ArrowRight, CalendarDays, CheckSquare, ExternalLink, FolderKanban, MoreHorizontal, Pencil, Store } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../shared/format'
import type { BoardColumn, BoardTask } from './api'
import { BOARD_PRIORITY_TONE, isBoardTaskOverdue } from './board'

type Props = {
  task: BoardTask
  prev?: BoardColumn
  next?: BoardColumn
  done: boolean
  assignee: string | null
  customer?: string
  project?: string
  canWrite: boolean
  onOpen: (t: BoardTask) => void
  onEdit: (t: BoardTask) => void
  onMove: (t: BoardTask, column: string) => void
  onArchive: (t: BoardTask) => void
}

const shortName = (name: string) => {
  const [first = '', last = ''] = name.split(' ')
  return last ? `${first} ${last[0]}.` : first
}

export function BoardTaskCard({ task, prev, next, done, assignee, customer, project, canWrite, onOpen, onEdit, onMove, onArchive }: Props) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id, disabled: !canWrite })
  const overdue = isBoardTaskOverdue(task, done)

  return (
    <article
      ref={setNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={cn(
        'group relative rounded-xl border bg-card p-3 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-card-hover motion-reduce:hover:translate-y-0',
        canWrite && 'cursor-grab touch-none active:cursor-grabbing',
        isDragging && 'z-50 rotate-1 opacity-80 shadow-lg',
        done && 'opacity-75',
      )}
      {...attributes}
      {...listeners}
      aria-roledescription={t('projects.kanban.draggable')}
    >
      <div className="flex items-center gap-2">
        {task.key ? <span className="font-mono text-[11px] text-muted-foreground">{task.key}</span> : null}
        <span className={cn('rounded-md px-1.5 py-0.5 text-[10px] font-semibold', BOARD_PRIORITY_TONE[task.priority])}>{t(`projects.rules.priority.${task.priority}`)}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="ml-auto size-7 text-muted-foreground" aria-label={t('projects.rowActions', { name: task.title })} onPointerDown={(e) => e.stopPropagation()}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48">
            <DropdownMenuItem onSelect={() => onOpen(task)}>
              <ExternalLink />
              {t('projects.kanban.open')}
            </DropdownMenuItem>
            {canWrite ? (
              <>
                <DropdownMenuItem onSelect={() => onEdit(task)}>
                  <Pencil />
                  {t('common.edit')}
                </DropdownMenuItem>
                {prev ? (
                  <DropdownMenuItem onSelect={() => onMove(task, prev.key)}>
                    <ArrowLeft />
                    {t('projects.kanban.moveTo', { status: prev.name })}
                  </DropdownMenuItem>
                ) : null}
                {next ? (
                  <DropdownMenuItem onSelect={() => onMove(task, next.key)}>
                    <ArrowRight />
                    {t('projects.kanban.moveTo', { status: next.name })}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onArchive(task)}>
                  <Archive />
                  {t('projects.board.archive')}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <button type="button" onClick={() => onOpen(task)} onPointerDown={(e) => e.stopPropagation()} className="mt-1.5 block w-full rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <h4 className="text-sm font-semibold leading-snug">{task.title}</h4>
        {task.description ? <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{task.description}</p> : null}
      </button>

      {customer || project ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {customer ? (
            <span className="inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground">
              <Store className="size-3 shrink-0" aria-hidden />
              <span className="truncate">{customer}</span>
            </span>
          ) : null}
          {project ? (
            <span className="inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground">
              <FolderKanban className="size-3 shrink-0" aria-hidden />
              <span className="truncate">{project}</span>
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="mt-2.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5">
          {assignee ? <Avatar name={assignee} className="size-6 [&>span]:text-[9px]" /> : null}
          <span className="truncate">{assignee ? shortName(assignee) : t('projects.work.unassigned')}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2.5">
          {task.checklistTotal > 0 ? (
            <span className={cn('inline-flex items-center gap-1 tabular-nums', task.checklistDone === task.checklistTotal && 'text-success')}>
              <CheckSquare className="size-3.5" aria-hidden />
              {task.checklistDone}/{task.checklistTotal}
            </span>
          ) : null}
          {task.dueAt ? (
            <span className={cn('inline-flex items-center gap-1 tabular-nums', overdue && 'font-medium text-destructive')}>
              <CalendarDays className="size-3.5" aria-hidden />
              {fmtDate(task.dueAt)}
            </span>
          ) : null}
        </span>
      </div>
    </article>
  )
}
